import { Paginated } from "@feathersjs/feathers/lib";
import { DataType, ErrorMessage, ICompany, ICompanyUser, IUser, Services } from "types";
import { makeObservable, observable, runInAction } from "mobx";
import client from "../feathers";
import { Data, User } from "./";

export class Company extends Data<ICompany> implements ICompany {

  title:      string = null;
  sparta_id?: number = null;

  user_count: number = 0;
  users:      User[] = [];
  constructor(data: ICompany) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Company, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      sparta_id:  observable,
      title:      observable,
      user_count: observable,
      users:      observable,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/
  loadUserCount = async (): Promise<boolean> => {
    try {
      const count: Paginated<ICompanyUser> = await client.service(Services.CompanyUser).find({
        query: {
          $limit:     0,
          company_id: this.id
        }
      });
      runInAction(() => this.user_count = count.total);
      return true;
    } catch(err) {
      console.error("Failed to load user count", err);
      return false;
    }
  }

  loadUsers = async (): Promise<boolean> => {
    try {
      const company_users: Paginated<ICompanyUser> = await client.service(Services.CompanyUser).find({
        query: {
          $select:    ["user_id"],
          company_id: this.id,
        }
      });
      const users: Paginated<IUser> = await client.service(Services.Users).find({
        query: {
          id: {$in: company_users.data.map(cu => cu.user_id)},
        }
      });
      runInAction(
        () => this.users = users.data.map(u => User.create(u))
          .sort((row1, row2) => row1.getFullName > row2.getFullName? 1 : -1)
      );
      return true;
    } catch (err) {
      console.error("Failed to load company users", err);
      return false;
    }
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public static get = async (id?: number): Promise<Company | Company[]> => {
    try {
      if(id) {
        return Company.create(await client.service(Services.Company).get(id));
      } else {
        const companys: Paginated<ICompany> = await client.service(Services.Company).find();
        if(!companys.total) return [];
        return companys.data.map(u => Company.create(u));
      }
    } catch (err) {
      console.error("Failed to load Companys", err);
      return null;
    }
  }

  public static create(data: ICompany): Company {
    return new Company(data);
  }

  protected fromData(data: ICompany): void {
    super.fromData(data);
    this.sparta_id = data.sparta_id && Number(data.sparta_id);
    this.title     = data.title;
  }

  public toData(): any {
    return {
      ...super.toData(),
      sparta_id: this.sparta_id || null,
      title:     this.title     || null,
    };
  }

  public toPatchData(patch: Partial<ICompany>): any {
    return {
      ...super.toPatchData(patch),
      sparta_id: "sparta_id" in patch ? this.sparta_id: undefined,
      title:     "title"     in patch ? this.title:     undefined,
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/

  public get canEdit(): boolean {
    return true;
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  public addUser = async (user: User): Promise<boolean|string> => {
    try {
      await client.service(Services.Users).create({
        ...user.toData(),
        company_id: this.id,
      });
      return true;
    } catch (err) {
      console.error("Failed to link user to company", err.message);
      if(err.message === ErrorMessage.UserExists) {
        return ErrorMessage.UserExists;
      }
      return false;
    }
  }
}
